import { useCallback, useContext, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'

import { GlobalToastType, kToastDurationMediumMs } from '@/constants/constants-ui'
import { AuthContext } from '@/context/auth-context'
import { selectMatterById } from '@/matter/store/selectors'
import { MatterDialogType, MattersActions } from '@/matter/store/slice'
import { canEditMatterMembers } from '@/matter/utils/matter-permissions'
import { openGlobalToast } from '@/store/slices/global-toast.slice'
import { useAppDispatch, useAppSelector } from '@/store/store-hooks'

import { ManageMembersModal } from './ManageMembersModal'
import { AddMembersStatus } from './types'
import { useManageMatterMembers } from './useManageMatterMembers'

/**
 * Container component that connects the ManageMembersModal to Redux state.
 * Reads dialog state, fetches member data, and handles close action.
 */
export default function ManageMembersDialogContainer() {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const { user, userAccountData } = useContext(AuthContext)
  const isOrgAdmin = userAccountData?.isOrgAdmin ?? false

  const dialogState = useAppSelector((state) => state.mattersState.dialog)

  const matter = useAppSelector((state) => selectMatterById(state, { matterId: dialogState.matterId ?? null }))
  const isOpen = dialogState.dialogType === MatterDialogType.ManageMembers

  const { collaborators, listStatus, orgUsers, addMembersStatus, addedCount, removedMemberName, actionError, addMembers, removeMember } =
    useManageMatterMembers(dialogState.matterId ?? '', isOpen)

  const matterName = matter?.name ?? ''

  const handleClose = useCallback(() => dispatch(MattersActions.closeDialog()), [dispatch])

  // On a fully successful add, confirm with a toast and close the modal.
  useEffect(() => {
    if (addMembersStatus !== AddMembersStatus.SUCCESS || !matter) return

    dispatch(
      openGlobalToast({
        type: GlobalToastType.SUCCESS,
        message: `${addedCount} ${addedCount === 1 ? 'member' : 'members'} added to matter: ${matter.name}`,
        durationMs: kToastDurationMediumMs,
        truncateAtLine: 2,
      })
    )
    dispatch(MattersActions.refreshMatterList({ forceRefresh: true }))
    handleClose()
  }, [addMembersStatus, addedCount, matter, dispatch, handleClose])

  // On a successful removal, confirm with a toast but keep the modal open.
  useEffect(() => {
    if (!removedMemberName || !matterName) return

    dispatch(
      openGlobalToast({
        type: GlobalToastType.SUCCESS,
        message: `${removedMemberName} removed from matter: ${matterName}`,
        durationMs: kToastDurationMediumMs,
        truncateAtLine: 2,
      })
    )
    dispatch(MattersActions.refreshMatterList({ forceRefresh: true }))
  }, [removedMemberName, matterName, dispatch])

  if (!isOpen || !matter) return null

  return (
    <ManageMembersModal
      open={isOpen}
      onClose={handleClose}
      matterName={matter.name}
      currentUserId={user?.uid ?? ''}
      collaborators={collaborators}
      listStatus={listStatus}
      orgUsers={orgUsers}
      onAddMembers={addMembers}
      addMembersStatus={addMembersStatus}
      onRemoveMember={removeMember}
      actionError={actionError}
      onManageOrganization={isOrgAdmin ? () => navigate('/dashboard/admin') : undefined}
      canEdit={canEditMatterMembers(matter)}
    />
  )
}
